import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Car, MapPin, Search, UserRound, X } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { fmtDate, STATUS, FLAGS } from "@/lib/constants";

const routeFor = (h) => {
  if (h.type === "pitch") return "/";
  if (h.type === "guest") return "/gasten";
  if (h.status === "checked_in") return "/verblijven";
  if (h.status === "checked_out") return "/vertrekkend";
  return "/reservaties";
};

const dot = (h) => (h.controle_nodig ? FLAGS.controle_nodig.color : h.status === "vrij" ? STATUS.vrij.color : h.status === "gereserveerd" || h.status === "reserved" ? STATUS.gereserveerd.color : STATUS.bezet.color);

export default function GlobalSearch() {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const isOverview = location.pathname === "/";  // kaart + planning: globale zoekdropdown
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const input = useRef(null);
  const [cursor, setCursor] = useState(0);

  // waarde in het veld: op het overzicht lokaal, op elk ander scherm uit de URL (?q=)
  const urlQ = params.get("q") || "";
  const value = isOverview ? q : urlQ;

  const onChange = (v) => {
    if (isOverview) { setQ(v); return; }
    const next = new URLSearchParams(params);
    if (v) next.set("q", v); else next.delete("q");
    setParams(next, { replace: true });
  };

  // Ctrl/Cmd+K of "/" opent het zoekveld
  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") || (e.key === "/" && !typing)) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // enkel op het overzicht de globale zoek-API + dropdown
  useEffect(() => {
    if (!isOverview) { setHits([]); setOpen(false); return; }
    if (q.trim().length < 2) { setHits([]); return; }
    const id = setTimeout(async () => {
      try { setHits((await api.get(`/search?q=${encodeURIComponent(q.trim())}`)).data); setCursor(0); setOpen(true); }
      catch (_) { setHits([]); }
    }, 250);
    return () => clearTimeout(id);
  }, [q, isOverview]);

  // bij wisselen naar het overzicht het lokale veld leegmaken
  useEffect(() => { if (isOverview) setQ(""); }, [isOverview]);

  const term = (h) => (h.match === "pitch" ? h.pitch_label : h.match === "plate" ? (h.plates || [])[0] : h.guest_name) || q.trim();
  const pick = (h) => { setOpen(false); setQ(""); navigate(`${routeFor(h)}?q=${encodeURIComponent(term(h))}`); };

  return (
    <div className="relative w-full" ref={box}>
      <div className="flex items-center gap-2 rounded-full bg-card/80 border border-white px-3 py-1.5 shadow-sm focus-within:border-primary/40 transition-colors">
        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          ref={input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => isOverview && hits.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Escape") { onChange(""); setOpen(false); }
            if (!isOverview) return;
            if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, hits.length - 1)); }
            if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
            if (e.key === "Enter" && hits[cursor]) pick(hits[cursor]);
          }}
          placeholder={isOverview ? t("search_placeholder") : t("search_in_screen")}
          className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          data-testid="global-search-input"
        />
        <span className="hidden xl:inline text-[10px] font-semibold text-muted-foreground border border-border rounded px-1.5 py-0.5 shrink-0">Ctrl K</span>
        {value && <button onClick={() => { onChange(""); setHits([]); }} data-testid="global-search-clear"><X className="h-3.5 w-3.5 text-muted-foreground" /></button>}
      </div>

      {isOverview && open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-1.5 z-50 max-h-96 overflow-auto cb-scroll rounded-xl border border-border bg-card shadow-xl py-1" data-testid="global-search-results">
          {hits.length === 0 ? (
            <p className="px-3 py-3 text-xs text-muted-foreground">{t("no_data")}</p>
          ) : hits.map((h, i) => (
            <button key={`${h.type}-${h.id}`} onMouseDown={() => pick(h)} onMouseEnter={() => setCursor(i)} data-testid={`search-hit-${h.type}-${h.pitch_label || h.guest_name}`}
              className={`w-full text-left px-3 py-2 transition-colors flex items-center gap-3 ${i === cursor ? "bg-secondary" : "hover:bg-secondary/60"}`}>
              <span className="shrink-0 grid place-items-center h-7 w-7 rounded-full" style={{ background: h.type === "guest" ? "#e2e8f0" : dot(h) }}>
                {h.type === "guest" ? <UserRound className="h-3.5 w-3.5 text-muted-foreground" /> : h.match === "plate" ? <Car className="h-3.5 w-3.5 text-white" /> : <MapPin className="h-3.5 w-3.5 text-white" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-foreground truncate">{h.guest_name || h.pitch_label}</span>
                  {h.pitch_label && h.guest_name && <span className="text-xs font-bold text-primary shrink-0">{h.pitch_label}</span>}
                </span>
                <span className="block text-[11px] text-muted-foreground truncate">
                  {h.type === "reservation" && `${fmtDate(h.arrival)} → ${fmtDate(h.departure)} · ${h.persons} ${t("persons").toLowerCase()}`}
                  {h.type === "pitch" && `${t("zone")} ${h.zone} · ${t(h.status)}`}
                  {h.type === "guest" && (h.email || h.phone || t("guest"))}
                  {h.match === "plate" && h.plates?.length ? ` · ${h.plates.join(", ")}` : ""}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
